import { call, fork, getContext, put, select, take, takeEvery } from 'redux-saga/effects';
import { authModalStatusSelector } from 'store/state/app/selectors';
import { setAuthModal, userLoginSocial, userLoginWithEmailAndPassword, signInSignUpWithEmail, setUserAuthEventData, resetUserAuthEventData } from 'store/state/app/actions';
import { LOGOUT, SET_AUTH_MODAL, USER_LOGIN_SOCIAL, USER_LOGIN_WITH_EMAIL_AND_PASSWORD, SIGN_IN_SIGN_UP_WITH_EMAIL } from 'store/state/app/types';
import { AuthenticationModalType, SocialVendor } from 'components/authentication/types';
import { RootAction } from 'store/state';
import { SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { setMutationResponse } from 'store/state/mutationsResponse/actions';
import { MutationType } from '../apiService/types';
import { AuthModalSource } from 'store/state/app';
import { routeNameSelector, routeSelector } from 'store/state/selectors/router';
import { Route, State as RouteState } from 'config/routes';
import { waitForUserChange } from '../routing/handlers/utils';

const NATIVE_AUTH_METHOD = 'native';

// smells, but required, not for export
let globalAuthModalSource: AuthModalSource;

type AuthMethods = SocialVendor | 'native';
type AuthMutationTypes =
  MutationType.AppleLogin |
  MutationType.CompleteAppleLogin |
  MutationType.GoogleLogin |
  MutationType.FacebookLogin |
  MutationType.LoginWithEmailAndPassword |
  MutationType.SignInSignUpWithEmail |
  MutationType.TempTokenAuth;

const getUserEmailFromAction = (action: ReturnType<typeof setMutationResponse>) => {
  switch (action.mutationType) {
    case MutationType.AppleLogin: {
      if (action.meta.response.data && action.meta.response.data.appleLogin) {
        const response = action.meta.response.data.appleLogin;
        return response.__typename === 'LoginOutcome' ? response.user.email : null;
      }
      return null;
    }
    case MutationType.CompleteAppleLogin: {
      if (action.meta.response.data && action.meta.response.data.completeAppleLogin) {
        const response = action.meta.response.data.completeAppleLogin;
        return response.__typename === 'LoginOutcome' ? response.user.email : null;
      }
      return null;
    }
    case MutationType.GoogleLogin: {
      const response = action.meta.response.data.googleLogin;
      return response.__typename === 'LoginOutcome' ? response.user.email : null;
    }

    case MutationType.FacebookLogin: {
      const response = action.meta.response.data.facebookLogin;
      return response.__typename === 'LoginOutcome' ? response.user.email : null;
    }

    case MutationType.LoginWithEmailAndPassword: {
      const response = action.meta.response.data.passwordLoginV3;
      return response.__typename === 'LoginOutcome' ? response.user.email : null;
    }

    case MutationType.SignInSignUpWithEmail: {
      const response = action.meta.response.data.loginV3;
      return response.__typename === 'LoginOutcome' ? response.user.email : null;
    }

    case MutationType.TempTokenAuth: {
      const response = action.meta.response.data.completeAutoLogin;
      return response.__typename === 'LoginOutcome' ? response.user.email : null;
    }

    default:
      return null;
  }
};

export const mutationTypeToAuthMethodMap: Record<AuthMutationTypes, AuthMethods> = {
  [MutationType.GoogleLogin]: 'google',
  [MutationType.FacebookLogin]: 'facebook',
  [MutationType.AppleLogin]: 'apple',
  [MutationType.CompleteAppleLogin]: 'apple',
  [MutationType.LoginWithEmailAndPassword]: NATIVE_AUTH_METHOD,
  [MutationType.SignInSignUpWithEmail]: NATIVE_AUTH_METHOD,
  [MutationType.TempTokenAuth]: NATIVE_AUTH_METHOD,
};

const UPLOAD_BULLETIN_ROUTE = new Set([
  Route.UploadBulletin,
]);

const isUploadRoute = (r: Route) => UPLOAD_BULLETIN_ROUTE.has(r);

function* authModalChangeWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    const previousModalType = yield select(authModalStatusSelector);
    const { payload, meta: { source: urlSource, isUserInitiated } } : ReturnType<typeof setAuthModal> = yield take(SET_AUTH_MODAL);

    const routeName = yield select(routeNameSelector);
    const source = isUploadRoute(routeName)
      ? AuthModalSource.UploadWizard
      : (
        routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : urlSource
      );

    const currentModalType = payload.type;


    if (source) {
      globalAuthModalSource = source;
    }

    if (globalAuthModalSource === AuthModalSource.AgentsLanding) {
      continue;
    }

    if (currentModalType === AuthenticationModalType.SignUp) {
      if (previousModalType === AuthenticationModalType.SignIn) {
        yield call(sendEvent, 'user_sign_up_form_change', 'user', { event: { source } });
      }

      if (!previousModalType) {
        yield call(sendEvent, 'user_sign_up_form_open', 'user', { event: { source } });
      }
    }

    if (currentModalType === AuthenticationModalType.Investors || currentModalType === AuthenticationModalType.GatedDeals) {
      if (!previousModalType) {
        yield call(sendEvent, 'user_sign_up_form_open', 'user', { event: { source } });
      }
    }

    if (currentModalType === AuthenticationModalType.SignIn && previousModalType === AuthenticationModalType.SignUp) {
      yield call(sendEvent, 'user_sign_in_form_change', 'user', { event: { source } });
    }

    // if modal is closed by user action
    if (!currentModalType && isUserInitiated) {
      if (previousModalType === AuthenticationModalType.SignIn) {
        yield call(sendEvent, 'user_sign_in_form_close', 'user', { event: { source } });
      }

      if (previousModalType === AuthenticationModalType.SignUp) {
        yield call(sendEvent, 'user_sign_up_form_close', 'user', { event: { source } });
      }

      if (previousModalType === AuthenticationModalType.Investors || previousModalType === AuthenticationModalType.GatedDeals) {
        yield call(sendEvent, 'user_sign_up_form_close', 'user', { event: { source } });
      }
    }
  }
}

function* signInNativeSubmissionWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    const action: ReturnType<typeof userLoginWithEmailAndPassword> = yield take(USER_LOGIN_WITH_EMAIL_AND_PASSWORD);
    const routeName = yield select(routeNameSelector);

    yield call(sendEvent, 'user_sign_in_submission', 'user', {
      event: {
        sign_in_method: NATIVE_AUTH_METHOD,
        email_address: action.payload.email,
        source: isUploadRoute(routeName)
          ? AuthModalSource.UploadWizard
          : (
            routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : globalAuthModalSource
          ),
      },
    });
  }

}

function* signInFromEmailSubmissionWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    yield take((action: RootAction) => action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.TempTokenAuth);

    const routeName = yield select(routeNameSelector);

    yield call(sendEvent, 'user_sign_in_submission', 'user', {
      event: {
        sign_in_method: mutationTypeToAuthMethodMap[MutationType.TempTokenAuth],
        source: isUploadRoute(routeName)
          ? AuthModalSource.UploadWizard
          : (
            routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : undefined
          ),
      },
    });
  }
}

function* signUpNativeSubmissionWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    const action: ReturnType<typeof signInSignUpWithEmail> = yield take(SIGN_IN_SIGN_UP_WITH_EMAIL);

    const routeName = yield select(routeNameSelector);

    if (globalAuthModalSource === AuthModalSource.AgentsLanding) {
      yield call(sendEvent, 'agent_identification_verify_email_click', 'agent', {
        agent: {},
      });
    }
    else {
      yield call(sendEvent, 'user_sign_up_submission', 'user', {
        event: {
          sign_up_method: NATIVE_AUTH_METHOD,
          email_address: action.payload.email,
          source: isUploadRoute(routeName)
            ? AuthModalSource.UploadWizard
            : (
              routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : globalAuthModalSource
            ),
        },
      });
    }
  }
}

function* socialAuthSubmissionWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    const action: ReturnType<typeof userLoginSocial> = yield take(USER_LOGIN_SOCIAL);

    const authModalStatus = yield select(authModalStatusSelector);
    const isSignIn = authModalStatus === AuthenticationModalType.SignIn;
    const eventAction = isSignIn ? 'user_sign_in_submission' : 'user_sign_up_submission';
    const methodName = isSignIn ? 'sign_in_method' : 'sign_up_method';

    const routeName = yield select(routeNameSelector);

    yield call(sendEvent, eventAction, 'user', {
      event: {
        [methodName]: action.payload,
        source: isUploadRoute(routeName)
          ? AuthModalSource.UploadWizard
          : (
            routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : (!isSignIn ? globalAuthModalSource : undefined)
          ),
      },
    });
  }
}

function* signInSuccessWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    const action: ReturnType<typeof setMutationResponse> = yield take(signInSuccessMutationPattern);

    if (
      action.meta.response.data
      && 'passwordLoginV3' in action.meta.response.data
      && action.meta.response.data.passwordLoginV3
      && 'errorMessage' in action.meta.response.data.passwordLoginV3
      && action.meta.response.data.passwordLoginV3.errorMessage) {
      continue; // skip if there is an error in response
    }

    const routeName = yield select(routeNameSelector);

    yield call(sendEvent, 'user_sign_in_success', 'user', {
      event: {
        sign_in_method: mutationTypeToAuthMethodMap[action.mutationType],
        email_address: getUserEmailFromAction(action),
        source: isUploadRoute(routeName)
          ? AuthModalSource.UploadWizard
          : (
            routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : globalAuthModalSource
          ),
      },
    });
  }
}

function* signUpSuccessWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    const action: ReturnType<typeof setMutationResponse> = yield take(signUpSuccessMutationPattern);
    yield call(waitForUserChange);

    const routeName = yield select(routeNameSelector);

    yield call(sendEvent, 'user_sign_up_success', 'user', {
      event: {
        sign_up_method: mutationTypeToAuthMethodMap[action.mutationType],
        email_address: getUserEmailFromAction(action),
        source: isUploadRoute(routeName)
          ? AuthModalSource.UploadWizard
          : (
            routeName === Route.InvestorsLoginPage ? AuthModalSource.Investors : globalAuthModalSource
          ),
      },
    });
  }
}

function* logoutWorker() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    yield take(LOGOUT);

    yield call(sendEvent, 'user_log_out', 'user');
  }
}

const emailVerificationCodePattern = (action: ReturnType<typeof setAuthModal>) => action && action.payload && action.payload.type === AuthenticationModalType.UserEmailVerification;

export const signInSuccessMutationPattern = (action: RootAction) => {
  if (action.type !== SET_MUTATION_RESPONSE ||
    (action.meta.response.errors && action.meta.response.errors.length)
  ) return false;

  if (action.mutationType === MutationType.LoginWithEmailAndPassword) return true; // verify is it sign in or sign up
  if (action.mutationType === MutationType.GoogleLogin) {
    const response = action.meta.response.data.googleLogin;
    if (response.__typename === 'LoginOutcome' && !response.signedUp) return true;
  }
  if (action.mutationType === MutationType.FacebookLogin) {
    const response = action.meta.response.data.facebookLogin;
    if (response.__typename === 'LoginOutcome' && !response.signedUp) return true;
  }
  if (action.mutationType === MutationType.AppleLogin) {
    const response = action.meta.response.data.appleLogin;
    if (response.__typename === 'LoginOutcome' && !response.signedUp) return true;
  }
  if (action.mutationType === MutationType.CompleteAppleLogin) {
    const response = action.meta.response.data.completeAppleLogin;
    if (response.__typename === 'LoginOutcome' && !response.signedUp) return true;
  }

  return false;
};

export const signUpSuccessMutationPattern = (action: RootAction) => {
  if (action.type !== SET_MUTATION_RESPONSE ||
    (action.meta.response.errors && action.meta.response.errors.length)
  ) return false;

  if (action.mutationType === MutationType.SignInSignUpWithEmail && action.meta.response.data.loginV3.__typename === 'NeedEmailVerificationError') return true;

  if (action.mutationType === MutationType.TempTokenAuth) return true; // verify is it sign in or sign up
  if (action.mutationType === MutationType.GoogleLogin) {
    const response = action.meta.response.data.googleLogin;
    if (response.__typename === 'LoginOutcome' && response.signedUp) return true;
  }
  if (action.mutationType === MutationType.AppleLogin) {
    const response = action.meta.response.data.appleLogin;
    if (response.__typename === 'LoginOutcome' && response.signedUp) return true;
  }
  if (action.mutationType === MutationType.CompleteAppleLogin) {
    const response = action.meta.response.data.completeAppleLogin;
    if (response.__typename === 'LoginOutcome' && response.signedUp) return true;
  }
  if (action.mutationType === MutationType.FacebookLogin) {
    const response = action.meta.response.data.facebookLogin;
    if (response.__typename === 'LoginOutcome' && response.signedUp) return true;
  }

  return false;
};

function* getEventSource(action?: ReturnType<typeof setAuthModal>) {
  const route: RouteState = yield select(routeSelector);

  const actionSource = action && action.meta && action.meta.source;
  const urlSource = route && route.params && route.params.source as AuthModalSource;

  switch (true) {
    case isUploadRoute(route.name):
      return AuthModalSource.UploadWizard;
    case route.name === Route.InvestorsLoginPage:
      return AuthModalSource.Investors;
    default:
      return (globalAuthModalSource || actionSource || urlSource);
  }
}

function* userAuthEventDataWatcher(
  action:
    | ReturnType<typeof setAuthModal>
    | ReturnType<typeof userLoginSocial>
    | ReturnType<typeof userLoginWithEmailAndPassword>
    | ReturnType<typeof signInSignUpWithEmail>
) {
  if (action.type === SET_AUTH_MODAL) {
    const authAction = action as ReturnType<typeof setAuthModal>;
    const currentModalType = authAction.payload.type;

    const resolvedSource = yield call(getEventSource, action);

    if (resolvedSource) {
      globalAuthModalSource = resolvedSource;

      yield put(setUserAuthEventData({
        source: resolvedSource,
      }));
    }

    if (!currentModalType) {
      globalAuthModalSource = undefined as any;
      yield put(resetUserAuthEventData());
    }

    return;
  }

  if (action.type === SIGN_IN_SIGN_UP_WITH_EMAIL) {
    const signUpAction = action as ReturnType<typeof signInSignUpWithEmail>;
    const resolvedSource = yield call(getEventSource);

    yield put(setUserAuthEventData({
      signUpMethod: NATIVE_AUTH_METHOD,
      emailAddress: signUpAction.payload.email,
      source: resolvedSource,
    }));
    return;
  }

  if (action.type === USER_LOGIN_WITH_EMAIL_AND_PASSWORD) {
    const signInAction = action as ReturnType<typeof userLoginWithEmailAndPassword>;
    const resolvedSource = yield call(getEventSource);

    yield put(setUserAuthEventData({
      signInMethod: NATIVE_AUTH_METHOD,
      emailAddress: signInAction.payload.email,
      source: signInAction.payload.loginSource as AuthModalSource || resolvedSource,
    }));
    return;
  }

  if (action.type === USER_LOGIN_SOCIAL) {
    const authModalStatus = yield select(authModalStatusSelector);
    const isSignIn = authModalStatus === AuthenticationModalType.SignIn;
    const socialAction = action as ReturnType<typeof userLoginSocial>;
    const resolvedSource = yield call(getEventSource);

    if (isSignIn) {
      yield put(setUserAuthEventData({
        signInMethod: socialAction.payload,
        source: resolvedSource,
      }));
    }
    else {
      yield put(setUserAuthEventData({
        signUpMethod: socialAction.payload,
        source: resolvedSource,
      }));
    }
  }
}

function* userAuthEventDataWorker() {
  yield takeEvery(
    [
      SET_AUTH_MODAL,
      USER_LOGIN_SOCIAL,
      USER_LOGIN_WITH_EMAIL_AND_PASSWORD,
      SIGN_IN_SIGN_UP_WITH_EMAIL,
    ],
    userAuthEventDataWatcher
  );
}

function* verificationCodeCloseWatcher() {
  const { sendEvent } = yield getContext('analytics');

  while (true) {
    yield take(emailVerificationCodePattern);

    const action: ReturnType<typeof setAuthModal> = yield take(setAuthModal);

    if (action && (!action.payload || action.payload.type === null)) {
      yield call(sendEvent, 'popup_close', 'popup', {
        event: {
          type: 'email_verification_code',
          source: 'sign_up',
        },
      });
    }
  }
}

export function* userAuthEventsWorker() {
  yield fork(userAuthEventDataWorker);
  yield fork(authModalChangeWorker);

  yield fork(signInNativeSubmissionWorker);
  yield fork(signInFromEmailSubmissionWorker);
  yield fork(signUpNativeSubmissionWorker);

  yield fork(socialAuthSubmissionWorker);

  yield fork(signInSuccessWorker);
  yield fork(signUpSuccessWorker);

  yield fork(verificationCodeCloseWatcher);

  yield fork(logoutWorker);
}
